import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { cn } from '@/shared/lib/utils';
import { Icon } from '@/shared/ui/icon';
import { Input } from '@/shared/ui/input';

import type {
  ListDateRangeFilterDef,
  ListEnumFilterDef,
  ListFilterFieldDef,
  ListFilterValue,
  ListNumberFilterDef,
  ListSelectorFilterDef,
  ListTextFilterDef,
} from '../model/types';

interface EditorProps<TDef extends ListFilterFieldDef> {
  def: TDef;
  value: ListFilterValue;
  onChange: (next: ListFilterValue) => void;
}

function Segmented({
  options,
  active,
  onPick,
}: {
  options: { value: string; label: string }[];
  active: string;
  onPick: (value: string) => void;
}) {
  return (
    <div className="phxlist-seg">
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          className="phxlist-seg-item"
          data-active={option.value === active || undefined}
          onClick={() => onPick(option.value)}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}

/** Editor prostého enum pole — vyhledávání + checkbox seznam options (anatomie 01 §5). */
export function EnumOptionsEditor({ def, value, onChange }: EditorProps<ListEnumFilterDef>) {
  const { t } = useTranslation('listPage');
  const [query, setQuery] = useState('');
  const { options, isLoading } = def.options.useOptions();
  const selected = value.ids ?? [];

  const normalized = query.trim().toLowerCase();
  const shown = normalized
    ? options.filter((option) => option.label.toLowerCase().includes(normalized))
    : options;

  const toggle = (id: string) =>
    onChange({
      ...value,
      ids: selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id],
    });

  return (
    <div className="p-phx-3 gap-phx-3 flex w-[230px] flex-col">
      <Input
        size="sm"
        autoFocus
        leadingIcon="search"
        placeholder={t('editors.enumSearch')}
        value={query}
        onChange={(e) => setQuery(e.target.value)}
      />
      <div className="flex max-h-[220px] flex-col gap-px overflow-auto">
        {isLoading ? (
          <div className="p-phx-4 text-phx-sm text-phx-text-faint">{t('editors.loading')}</div>
        ) : (
          shown.map((option) => {
            const checked = selected.includes(option.id);
            return (
              <button
                key={option.id}
                type="button"
                onClick={() => toggle(option.id)}
                className={cn(
                  'gap-phx-3 px-phx-3 rounded-phx flex h-[26px] items-center text-left',
                  'text-phx-md text-phx-text hover:bg-phx-surface-2 cursor-pointer border-0 bg-transparent'
                )}
              >
                <span
                  className={cn(
                    'inline-flex h-[13px] w-[13px] items-center justify-center rounded-[3px] border',
                    checked
                      ? 'border-phx-primary bg-phx-primary text-phx-primary-fg'
                      : 'border-phx-border-3 bg-phx-surface'
                  )}
                >
                  {checked ? <Icon name="check" size={9} strokeWidth={2.5} /> : null}
                </span>
                <span className="min-w-0 flex-1 truncate">{option.label}</span>
                {option.code ? (
                  <span className="text-phx-xs text-phx-text-faint font-mono">{option.code}</span>
                ) : null}
              </button>
            );
          })
        )}
        {!isLoading && shown.length === 0 ? (
          <div className="p-phx-4 text-phx-sm text-phx-text-faint">
            {t('editors.noMatch', { query })}
          </div>
        ) : null}
      </div>
      {selected.length > 0 ? (
        <div className="border-phx-border pt-phx-3 flex items-center justify-between border-t">
          <span className="text-phx-xs text-phx-text-muted">
            {t('editors.selectedCount', { count: selected.length })}
          </span>
          <button
            type="button"
            className="text-phx-xs text-phx-text-2 hover:text-phx-text cursor-pointer border-0 bg-transparent"
            onClick={() => onChange({ ...value, ids: [] })}
          >
            {t('editors.clear')}
          </button>
        </div>
      ) : null}
    </div>
  );
}

/** Editor textového pole — přepínač obsahuje/je přesně + textové pole (anatomie 01 §5). */
export function TextFilterEditor({ def, value, onChange }: EditorProps<ListTextFilterDef>) {
  const { t } = useTranslation('listPage');
  return (
    <div className="p-phx-4 gap-phx-3 flex w-[240px] flex-col">
      <Segmented
        options={[
          { value: 'contains', label: t('editors.opContains') },
          { value: 'eq', label: t('editors.opEquals') },
        ]}
        active={value.op === 'eq' ? 'eq' : 'contains'}
        onPick={(op) => onChange({ ...value, op: op as 'contains' | 'eq' })}
      />
      <Input
        size="sm"
        autoFocus
        placeholder={`${def.label}…`}
        value={value.text ?? ''}
        onChange={(e) => onChange({ ...value, text: e.target.value })}
      />
    </div>
  );
}

/** Editor číselného pole — přepínač ≥ / = / ≤ + číselné pole s jednotkou (anatomie 01 §5). */
export function NumberFilterEditor({ def, value, onChange }: EditorProps<ListNumberFilterDef>) {
  return (
    <div className="p-phx-4 gap-phx-3 flex w-[220px] flex-col">
      <Segmented
        options={[
          { value: 'gte', label: '≥' },
          { value: 'eq', label: '=' },
          { value: 'lte', label: '≤' },
        ]}
        active={value.op ?? 'gte'}
        onPick={(op) => onChange({ ...value, op: op as 'gte' | 'eq' | 'lte' })}
      />
      <div className="gap-phx-2 flex items-center">
        <Input
          size="sm"
          type="number"
          autoFocus
          placeholder="0"
          value={value.number ?? ''}
          onChange={(e) =>
            onChange({
              ...value,
              number: e.target.value === '' ? null : Number(e.target.value),
            })
          }
        />
        {def.unit ? (
          <span className="text-phx-sm text-phx-text-muted whitespace-nowrap">{def.unit}</span>
        ) : null}
      </div>
    </div>
  );
}

/** Editor rozsahu dat — presety šablony + od/do, obě meze volitelné (anatomie 01 §5). */
export function DateRangeFilterEditor({
  def,
  value,
  onChange,
}: EditorProps<ListDateRangeFilterDef>) {
  return (
    <div className="p-phx-4 gap-phx-3 flex w-[250px] flex-col">
      {def.presets && def.presets.length > 0 ? (
        <div className="gap-phx-2 flex flex-wrap">
          {def.presets.map((preset) => (
            <button
              key={preset.label}
              type="button"
              className={cn(
                'rounded-phx border-phx-border-2 bg-phx-surface text-phx-xs text-phx-text h-[22px] cursor-pointer border',
                'px-phx-3 hover:border-phx-border-3 hover:bg-phx-surface-2'
              )}
              onClick={() => onChange({ ...value, ...preset.range() })}
            >
              {preset.label}
            </button>
          ))}
        </div>
      ) : null}
      <div className="gap-phx-2 flex items-center">
        <Input
          size="sm"
          type="date"
          value={value.from ?? ''}
          onChange={(e) => onChange({ ...value, from: e.target.value || undefined })}
        />
        <span className="text-phx-text-muted">→</span>
        <Input
          size="sm"
          type="date"
          value={value.to ?? ''}
          onChange={(e) => onChange({ ...value, to: e.target.value || undefined })}
        />
      </div>
    </div>
  );
}

/**
 * Editor selector pole — plnohodnotné selectorové pole (rows layout, dropdown s vyhledáváním
 * a stránkováním) controlled PŘÍMO nad uloženými *Ref modely (`items`) — vykreslení okamžité,
 * bez čekání na API. `ids` se odvozují z items přes `getId` při každé změně.
 */
function SelectorFilterEditor<TItem>({
  def,
  value,
  onChange,
}: {
  def: ListSelectorFilterDef<TItem>;
  value: ListFilterValue;
  onChange: (next: ListFilterValue) => void;
}) {
  const Selector = def.selector.Selector;
  const items = (value.items ?? []) as TItem[];
  return (
    <div className="p-phx-3 w-[280px]">
      <Selector
        mode="multi"
        label=""
        selected={items}
        onChange={(next) =>
          onChange({
            ...value,
            items: [...next],
            ids: next.map((item) => String(def.getId(item))),
          })
        }
      />
    </div>
  );
}

/** Dispatch editoru podle typu pole — stejné editory používá chip popover i panel filtrů. */
export function FilterValueEditor({
  def,
  value,
  onChange,
}: {
  def: ListFilterFieldDef;
  value: ListFilterValue;
  onChange: (next: ListFilterValue) => void;
}) {
  switch (def.type) {
    case 'enum':
      return <EnumOptionsEditor def={def} value={value} onChange={onChange} />;
    case 'selector':
      return <SelectorFilterEditor def={def} value={value} onChange={onChange} />;
    case 'text':
      return <TextFilterEditor def={def} value={value} onChange={onChange} />;
    case 'number':
      return <NumberFilterEditor def={def} value={value} onChange={onChange} />;
    case 'daterange':
      return <DateRangeFilterEditor def={def} value={value} onChange={onChange} />;
  }
}
